import { toStartCase } from './common';

/**
 * TEMPORARY COMPATIBILITY LAYER
 *
 * Why this exists:
 * - Some entity attribute keys are currently backend-driven BPS field names
 *   (for example: bpsJobSequenceNo) that are not user-friendly for UI display.
 *
 * Removal plan (once entity type attributes are complete and user-friendly):
 * 1) Remove BpsNameMapping and naming helpers from this file.
 * 2) Use backend-provided labels directly in UI/config flows.
 * 3) Remove all imports/usages of getAttributeDefaultName/getAttributeDisplayName.
 */
export const BpsNameMapping: Record<string, string> = {
  bpsEquipmentScheduleID: 'equipmentScheduleID',
  bpsProductionJobID: 'scheduleProductionJobID',
  bpsJobSequenceNo: 'scheduleJobSequenceNo',
  bpsPackID: 'schedulePackID',
  bpsParentPackID: 'scheduleParentPackID',
  bpsScheduleStartTime: 'scheduleStartTime',
  bpsScheduleEndTime: 'scheduleEndTime',
  bpsProductionProcessID: 'scheduleProductionProcessID',
  bpsBatchID: 'scheduleBatchID',
  bpsPlannedShipmentID: 'schedulePlannedShipmentID',
};

/**
 * Returns the default name to use in configuration state.
 * Prefer mapped value while this compatibility layer exists.
 */
export const getAttributeDefaultName = (
  attribute: string,
  fallback?: string,
): string => BpsNameMapping[attribute] || fallback || attribute;

/**
 * Returns UI-ready label formatting for the resolved default name.
 */
export const getAttributeDisplayName = (
  attribute: string,
  fallback?: string,
): string => toStartCase(getAttributeDefaultName(attribute, fallback));

/**
 * Resolves a persisted config label for an attribute.
 * - Preserves explicit user-custom labels.
 * - Upgrades legacy auto-generated labels (e.g. "Bps Planned Shipment ID")
 *   to the mapped default name when mapping exists.
 */
export const resolveAttributeConfigLabel = (
  attribute: string,
  configuredLabel?: string,
  fallback?: string,
): string => {
  const rawPathDisplay = toStartCase(attribute.trim().split('.').join(' '));
  const mappedDefault = getAttributeDefaultName(
    attribute,
    fallback || rawPathDisplay,
  );

  if (!configuredLabel) {
    return mappedDefault;
  }

  const normalizedConfigured = configuredLabel.trim().toLowerCase();
  const normalizedRawPath = rawPathDisplay.trim().toLowerCase();

  if (normalizedConfigured === normalizedRawPath) {
    return mappedDefault;
  }

  return configuredLabel;
};
